import React, {
  useState,
  useRef,
  useEffect,
  useMemo,
  useCallback,
} from 'react';
import { motion, AnimatePresence } from 'framer-motion';

type MenuType = 'food' | 'drinks';

interface MenuTypeOption {
  value: MenuType;
  label: string;
}

interface MenuTypeDropdownProps {
  value: MenuType;
  onChange: (value: MenuType) => void;
  className?: string;
}

const MENU_TYPE_OPTIONS: MenuTypeOption[] = [
  { value: 'food', label: 'Food' },
  { value: 'drinks', label: 'Drinks' },
];

export const MenuTypeDropdown: React.FC<MenuTypeDropdownProps> = ({
  value,
  onChange,
  className = '',
}) => {
  const [isOpen, setIsOpen] = useState(false);

  const dropdownRef = useRef<HTMLDivElement>(null);

  const selectedOption = useMemo(
    () => MENU_TYPE_OPTIONS.find((option) => option.value === value),
    [value]
  );

  /**
   * Close dropdown on outside click
   */
  useEffect(() => {
    const handleOutsideClick = (event: MouseEvent | PointerEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    };

    document.addEventListener('pointerdown', handleOutsideClick);

    return () => {
      document.removeEventListener('pointerdown', handleOutsideClick);
    };
  }, []);

  /**
   * Handle option selection
   */
  const handleSelect = useCallback(
    (option: MenuTypeOption) => {
      onChange(option.value);
      setIsOpen(false);
    },
    [onChange]
  );

  /**
   * Keyboard accessibility
   */
  const handleKeyDown = (event: React.KeyboardEvent<HTMLButtonElement>) => {
    if (event.key === 'Escape') {
      setIsOpen(false);
    }

    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      setIsOpen((prev) => !prev);
    }
  };

  return (
    <div
      ref={dropdownRef}
      className={`relative w-full max-w-[120px] ${className}`}
    >
      {/* Trigger Button */}
      <button
        type="button"
        aria-haspopup="listbox"
        aria-expanded={isOpen}
        onClick={() => setIsOpen((prev) => !prev)}
        onKeyDown={handleKeyDown}
        className="
          flex items-center justify-between
          w-full
          min-h-[44px]

          px-3 sm:px-4
          py-2.5

          rounded-xl
          border

          bg-[var(--bg-secondary)]
          border-[var(--border-color)]

          text-sm sm:text-base
          font-medium
          text-[var(--text-primary)]

          transition-all duration-200 ease-out

          hover:border-[#EF4421]
          hover:bg-[var(--bg-hover)]

          focus:outline-none
          focus:ring-4
          focus:ring-[#EF4421]/20

          active:scale-[0.98]
        "
      >
        <span className="truncate">
          {selectedOption?.label}
        </span>

        <motion.svg
          animate={{ rotate: isOpen ? 180 : 0 }}
          transition={{ duration: 0.2 }}
          className="
            ml-2
            h-4 w-4 shrink-0
            text-[var(--text-secondary)]
          "
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M19 9l-7 7-7-7"
          />
        </motion.svg>
      </button>

      {/* Dropdown */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            role="listbox"
            initial={{
              opacity: 0,
              y: -8,
              scale: 0.98,
            }}
            animate={{
              opacity: 1,
              y: 0,
              scale: 1,
            }}
            exit={{
              opacity: 0,
              y: -8,
              scale: 0.98,
            }}
            transition={{
              duration: 0.18,
              ease: 'easeOut',
            }}
            className="
              absolute left-0 right-0
              mt-2
              z-50

              overflow-hidden
              rounded-xl
              border

              border-[var(--border-color)]
              bg-[var(--bg-primary)]

              shadow-xl
              backdrop-blur-md
            "
          >
            {MENU_TYPE_OPTIONS.map((option) => {
              const isActive = option.value === value;

              return (
                <button
                  key={option.value}
                  role="option"
                  aria-selected={isActive}
                  type="button"
                  onClick={() => handleSelect(option)}
                  className={`
                    w-full
                    px-4 py-3

                    text-left
                    text-sm sm:text-base

                    transition-colors duration-150

                    ${
                      isActive
                        ? 'bg-[#FEECE9] text-[#EF4421] font-semibold'
                        : `
                          text-[var(--text-primary)]
                          hover:bg-[var(--bg-hover)]
                        `
                    }
                  `}
                >
                  {option.label}
                </button>
              );
            })}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};
